import {
  EventExhibitor,
  ExhibitorProfilesPackNumber,
  ExhibitorProfilesPackPrestationType,
  isExhibitorProfilesPackPrestationType,
} from "@ac-expo/api-types";
import { BackofficePrestationType } from "@prisma/client";
import { isSameDay } from "date-fns";
import { getExhibitorProfilesPackNumber } from "./prestations";
import {
  MAX_EVENT_PRODUCTS_NUMBER,
  MAX_PRODUCT_IMAGES,
} from "./products.utils";

export const MAX_PROMOTED_EXHIBITORS_PER_DAY = 5;
export const MAX_EVENT_DOCUMENTS_NUMBER = 3;

export const getEventExhibitorPackNumber = (
  eventExhibitor?: Pick<EventExhibitor, "exhibitorProfilesPack">
): ExhibitorProfilesPackNumber | null => {
  if (!eventExhibitor) return null;

  return getExhibitorProfilesPackNumber(eventExhibitor.exhibitorProfilesPack);
};

export type PackFeatures = {
  hasDetailedExhibitorProfile: boolean;
  maxProductCount: number;
  maxProductImages: number;
  hasProductLink: boolean;
  hasProductDescription: boolean;
  hasCommercialApp: boolean;
  hasCustomAppearance: boolean;
  maxDocuments: number;
  hasProVisitors: boolean;
  hasVisitorNotifications: boolean;
  hasEmailNotification: boolean;
};

export const getPackFeatures = (
  exhibitorPackIdentifier:
    | ExhibitorProfilesPackNumber
    | 0
    | null
    | undefined
    | ExhibitorProfilesPackPrestationType
    | BackofficePrestationType
): PackFeatures => {
  if (
    typeof exhibitorPackIdentifier === "string" &&
    !isExhibitorProfilesPackPrestationType(exhibitorPackIdentifier)
  ) {
    exhibitorPackIdentifier = null;
  }

  const exhibitorPackNumber =
    typeof exhibitorPackIdentifier === "string"
      ? getExhibitorProfilesPackNumber(exhibitorPackIdentifier)
      : exhibitorPackIdentifier;

  const features: PackFeatures = {
    hasDetailedExhibitorProfile: false,
    maxProductCount: MAX_EVENT_PRODUCTS_NUMBER,
    maxProductImages: 1,
    hasProductLink: false,
    hasProductDescription: false,
    hasCommercialApp: false,
    hasCustomAppearance: false,
    maxDocuments: 0,
    hasProVisitors: false,
    hasVisitorNotifications: false,
    hasEmailNotification: false,
  };

  if (!exhibitorPackNumber) {
    return features;
  }

  if (exhibitorPackNumber >= 1) {
    features.hasDetailedExhibitorProfile = true;
  }

  if (exhibitorPackNumber >= 2) {
    features.maxProductCount = Number.MAX_VALUE;
    features.maxProductImages = MAX_PRODUCT_IMAGES;
    features.hasProductLink = true;
    features.hasProductDescription = true;
    features.hasCustomAppearance = true;
    features.maxDocuments = MAX_EVENT_DOCUMENTS_NUMBER;
    features.hasProVisitors = true;
    features.hasEmailNotification = true;
  }

  if (exhibitorPackNumber >= 3) {
    features.hasCommercialApp = true;
    features.maxDocuments = Number.MAX_VALUE;
    features.hasVisitorNotifications = true;
  }

  return features;
};

export const isDateDisabled = (date: Date, disabledDates?: Date[]): boolean => {
  if (!disabledDates || disabledDates.length === 0) return false;
  return disabledDates.some((disabledDate) => isSameDay(disabledDate, date));
};

export const filterAvailableDates = (
  dates: Date[],
  disabledDates?: Date[]
): Date[] => {
  if (!disabledDates || disabledDates.length === 0) return dates;
  return dates.filter((date) => !isDateDisabled(date, disabledDates));
};
